import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AvailableRole, AuthResponse } from '@/types'
import { authApi } from '@/services/api'
import type { Role } from '@/types/role'
import { clearTokens, flushDurableTokens, getAccessToken, getRefreshToken, setTokens } from '@/utils/tokenStorage'
import { armPasskeyOffer, isFaceIdClient, skipAutoFaceId } from '@/utils/deviceUnlock'
import { refreshAccessToken } from '@/services/api/client'
import { isStandaloneDisplay, webAuthnAvailable } from '@/utils/pwaDisplay'
import { clearUaTalkLocalHides } from '@/talk/uaTalkList'
import { clearPendingLoginCode } from '@/utils/pendingLoginCode'


const getToken = () => getAccessToken()
const ROLES_STALE_THRESHOLD_MS = 5 * 60 * 1000
const ENGINEER_SCHOOLS_KEY = 'engineer_schools'

export function rememberEngineerSchools(response: {
  needs_school_selection?: boolean
  schools?: Array<{ id: string; name: string }>
} | null | undefined): boolean {
  if (!response?.needs_school_selection) return false
  sessionStorage.setItem(ENGINEER_SCHOOLS_KEY, JSON.stringify(response.schools || []))
  return true
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoginSubmitting: boolean
  error: string | null
  availableRoles: AvailableRole[] | null
  availableRolesFetchedAt: number | null
  isFetchingRoles: boolean
  rolesFetchError: string | null
  pendingWebAuthnEnroll: boolean
  login: (email: string) => Promise<void>
  verify: (token: string) => Promise<import('@/types').AuthResponse>
  verifyCode: (email: string, code: string) => Promise<AuthResponse>
  completeSession: (response: AuthResponse, opts?: { enroll?: boolean }) => Promise<AuthResponse>
  clearWebAuthnEnroll: () => void
  logout: () => Promise<void>
  logoutAll: () => Promise<void>
  fetchUser: () => Promise<void>
  refreshToken: () => Promise<boolean>
  clearError: () => void
  fetchAvailableRoles: () => Promise<void>
  switchRole: (role: Role) => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isLoginSubmitting: false,
      error: null,
      availableRoles: null,
      availableRolesFetchedAt: null,
      isFetchingRoles: false,
      rolesFetchError: null,
      pendingWebAuthnEnroll: false,

      login: async (email: string) => {
        set({ isLoginSubmitting: true, error: null })
        try {
          await authApi.requestLogin({
            email,
            frontend_url: window.location.origin,
          })
          set({ isLoginSubmitting: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Login fehlgeschlagen',
            isLoginSubmitting: false 
          })
          throw error
        }
      },

      completeSession: async (response: AuthResponse, opts?: { enroll?: boolean }) => {
        clearUaTalkLocalHides()
        clearPendingLoginCode()
        rememberEngineerSchools(response)
        setTokens(response.access_token, response.refresh_token)
        await flushDurableTokens()
        if (opts?.enroll && isFaceIdClient()) armPasskeyOffer()
        const enroll = Boolean(opts?.enroll && webAuthnAvailable())
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          isLoginSubmitting: false,
          rolesFetchError: null,
          pendingWebAuthnEnroll: enroll,
        })
        if (isStandaloneDisplay()) {
          try {
            await navigator.storage?.persist?.()
          } catch {
            /* iOS often ignores */
          }
        }
        await get().fetchAvailableRoles()
        return response
      },

      verify: async (token: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.verify({ token })
          return await get().completeSession(response, { enroll: true })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Verifizierung fehlgeschlagen',
            isLoading: false 
          })
          throw error
        }
      },

      verifyCode: async (email: string, code: string) => {
        set({ isLoginSubmitting: true, error: null })
        try {
          const normalized = code.replace(/[\s-]/g, '')
          const response = await authApi.verifyCode({ email, code: normalized })
          return await get().completeSession(response, { enroll: true })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Verifizierung fehlgeschlagen',
            isLoginSubmitting: false,
          })
          throw error
        }
      },

      clearWebAuthnEnroll: () => set({ pendingWebAuthnEnroll: false }),

      logout: async () => {
        try {
          await authApi.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          skipAutoFaceId()
          clearUaTalkLocalHides()
          clearTokens()
          set({ user: null, isAuthenticated: false, availableRoles: null, availableRolesFetchedAt: null, isFetchingRoles: false, rolesFetchError: null })
        }
      },

      logoutAll: async () => {
        try {
          await authApi.logoutAll()
        } catch (error) {
          console.error('Logout all error:', error)
        } finally {
          skipAutoFaceId()
          clearUaTalkLocalHides()
          clearTokens()
          set({ user: null, isAuthenticated: false, availableRoles: null, availableRolesFetchedAt: null, isFetchingRoles: false, rolesFetchError: null })
        }
      },

      fetchUser: async () => {
        let token = getToken()
        if (!token) {
          const refreshed = await get().refreshToken()
          token = getToken()
          if (!refreshed || !token) {
            if (getRefreshToken() && get().isAuthenticated && get().user) {
              set({ isLoading: false })
              return
            }
            clearTokens()
            set({ user: null, isAuthenticated: false, isLoading: false, isLoginSubmitting: false, availableRoles: null, availableRolesFetchedAt: null, isFetchingRoles: false, rolesFetchError: null })
            return
          }
        }
        set({ isLoading: true })
        try {
          const user = await authApi.getCurrentUser()
          if (get().isAuthenticated && get().user && getToken() && getToken() !== token) {
            set({ isLoading: false })
            return
          }
          set({ user, isAuthenticated: true, isLoading: false, rolesFetchError: null })
          const state = get()
          const isStale = !state.availableRolesFetchedAt ||
            (Date.now() - state.availableRolesFetchedAt > ROLES_STALE_THRESHOLD_MS)
          if (isStale) {
            await get().fetchAvailableRoles()
          }
        } catch {
          if (get().isAuthenticated && get().user && getToken() && getToken() !== token) {
            set({ isLoading: false })
            return
          }
          const refreshed = await get().refreshToken()
          if (refreshed) {
            try {
              const user = await authApi.getCurrentUser()
              set({ user, isAuthenticated: true, isLoading: false, rolesFetchError: null })
              await get().fetchAvailableRoles()
              return
            } catch {
              /* fall through */
            }
          }
          if (getRefreshToken() && get().isAuthenticated && get().user) {
            set({ isLoading: false })
          } else {
            clearTokens()
            set({ user: null, isAuthenticated: false, isLoading: false, isLoginSubmitting: false, availableRoles: null, availableRolesFetchedAt: null, isFetchingRoles: false, rolesFetchError: null })
          }
        }
      },

      refreshToken: async () => {
        if (!getRefreshToken()) return false
        const access = await refreshAccessToken()
        if (access) return true
        if (!getRefreshToken()) {
          set({ user: null, isAuthenticated: false })
        }
        return false
      },

      clearError: () => set({ error: null }),

      fetchAvailableRoles: async () => {
        const token = getToken()
        if (!token) return
        set({ isFetchingRoles: true, rolesFetchError: null })
        try {
          const response = await authApi.getMyRoles()
          const currentState = get()
          const updates: Partial<AuthState> = {
            availableRoles: response.roles,
            isFetchingRoles: false,
            rolesFetchError: null,
            availableRolesFetchedAt: Date.now(),
          }
          const overlayOn = Boolean(currentState.user?.acting_role || currentState.user?.acting_teacher_id || currentState.user?.acting_user_id)
          if (!overlayOn && currentState.user && response.active_role !== currentState.user.role) {
            updates.user = { ...currentState.user, role: response.active_role }
          }
          set(updates)
        } catch (error) {
          console.error('Failed to fetch available roles:', error)
          set({ isFetchingRoles: false, rolesFetchError: 'Rollen konnten nicht geladen werden' })
        }
      },

      switchRole: async (role: Role) => {
        set({ isLoading: true, error: null })
        try {
          const current = get().user
          if (current?.acting_role || current?.acting_teacher_id) {
            const { ultraadminApi } = await import('@/services/api/ultraadmin')
            const res = await ultraadminApi.switchActAsRole(role)
            setTokens(res.access_token, res.refresh_token)
            rememberEngineerSchools(res.user)
            set({ user: res.user, isAuthenticated: true, isLoading: false })
            return
          }
          const updatedUser = await authApi.switchRole(role)
          if (updatedUser.access_token) {
            setTokens(updatedUser.access_token, updatedUser.refresh_token)
          }
          rememberEngineerSchools(updatedUser)
          set({ user: updatedUser, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Rollenwechsel fehlgeschlagen',
            isLoading: false,
          })
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated, user: state.user, availableRoles: state.availableRoles, availableRolesFetchedAt: state.availableRolesFetchedAt }),
      merge: (persistedState, currentState) => {
        if (currentState.isAuthenticated && currentState.user) return currentState
        return { ...currentState, ...(persistedState as object) }
      },
    }
  )
)

export function waitForAuthHydration(): Promise<void> {
  if (useAuthStore.persist.hasHydrated()) return Promise.resolve()
  return new Promise((resolve) => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      unsub()
      resolve()
    })
  })
}
